import { Team, TeamMember, TeamRole, TodoTasksBoard } from '@demind-inc/core';
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TeamInviteAcceptReq, TeamShareSettingsUpdateReq } from '../api';
import {
  ResolveTeamInviteResult,
  useAcceptTeamInvite,
  useDeleteTeamBoard,
  useCreateTeamBoard,
  useCreateTeam,
  useCreateTeamInvite,
  useDeleteTeam,
  useLeaveTeam,
  useRemoveTeamMember,
  useResolveTeamInvite,
  useTransferTeamOwnership,
  useUpdateTeam,
  useUpdateTeamBoard,
  useUpdateTeamMemberRole,
  useUpdateTeamShareSettings,
} from '../mutations';
import { TeamListItem, useTeam, useTeams } from '../queries';
import { useAuthContext } from './AuthProvider';

interface ITeamContext {
  teams: TeamListItem[];
  selectedTeam: Team | null;
  selectedMembership: TeamMember | null;
  selectedMembers: TeamMember[];
  selectedBoards: TodoTasksBoard[];
  activeTeamName: string | null;
  hasTeams: boolean;
  createTeamError: string | null;
  createTeamInviteError: string | null;
  teamInviteResolveError: string | null;
  acceptTeamInviteError: string | null;
  updateTeamError: string | null;
  leaveTeamError: string | null;
  teamMemberActionError: string | null;
  teamProjectActionError: string | null;
  isCreatingTeam: boolean;
  isCreatingTeamInvite: boolean;
  isResolvingTeamInvite: boolean;
  isAcceptingTeamInvite: boolean;
  isTeamsLoading: boolean;
  isTeamLoading: boolean;
  teamListError: string | null;
  teamDetailError: string | null;
  updateTeamShareSettingsError: string | null;
  createTeam: (teamName: string) => Promise<string | null>;
  createTeamInvite: (
    teamId: string,
    inviteEmail: string,
    role?: 'admin' | 'member'
  ) => Promise<string | null>;
  resolveTeamInvite: (inviteCode: string) => Promise<ResolveTeamInviteResult | null>;
  acceptTeamInvite: (teamInviteAcceptReq: TeamInviteAcceptReq) => Promise<string | null>;
  updateTeamShareSettings: (
    teamId: string,
    teamShareSettingsUpdateReq: TeamShareSettingsUpdateReq
  ) => Promise<TeamMember | null>;
  updateTeamName: (teamId: string, teamName: string) => Promise<Team | null>;
  leaveTeam: (teamId: string) => Promise<boolean>;
  deleteTeam: (teamId: string) => Promise<boolean>;
  updateTeamMemberRole: (
    teamId: string,
    memberUserId: string,
    role: Exclude<TeamRole, 'owner'>
  ) => Promise<TeamMember | null>;
  removeTeamMember: (teamId: string, memberUserId: string) => Promise<TeamMember | null>;
  transferTeamOwnership: (teamId: string, newOwnerUserId: string) => Promise<boolean>;
  createTeamBoard: (teamId: string, boardName: string) => Promise<boolean>;
  updateTeamBoard: (teamId: string, boardId: string, boardName: string) => Promise<boolean>;
  deleteTeamBoard: (teamId: string, boardId: string) => Promise<boolean>;
  clearCreateTeamError: () => void;
  clearCreateTeamInviteError: () => void;
  clearTeamInviteResolveError: () => void;
  clearAcceptTeamInviteError: () => void;
  clearUpdateTeamShareSettingsError: () => void;
  clearUpdateTeamError: () => void;
  clearLeaveTeamError: () => void;
  clearTeamMemberActionError: () => void;
  clearTeamProjectActionError: () => void;
  isUpdatingTeamShareSettings: boolean;
  isUpdatingTeam: boolean;
  isLeavingTeam: boolean;
  isDeletingTeam: boolean;
  isUpdatingTeamMemberRole: boolean;
  isRemovingTeamMember: boolean;
  isTransferringTeamOwnership: boolean;
  isCreatingTeamBoard: boolean;
  isUpdatingTeamBoard: boolean;
  isDeletingTeamBoard: boolean;
}

type ApiError = {
  message?: string;
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
};

const parseTeamError = (error: unknown, fallbackMessage: string) => {
  const parsedError = error as ApiError;
  const responseData = parsedError.response?.data;
  const rawMessage = responseData?.error || responseData?.message || parsedError.message || '';

  if (!rawMessage) {
    return fallbackMessage;
  }

  if (rawMessage.toLowerCase().includes('forbidden')) {
    return fallbackMessage;
  }

  return rawMessage;
};

export const TeamContext = createContext({} as ITeamContext);
export const useTeamContext = () => useContext(TeamContext);

export const TeamProvider = ({ children }: { children: ReactNode }) => {
  const { teamId } = useParams();
  const { user, isUserFetching } = useAuthContext();
  const [createTeamError, setCreateTeamError] = useState<string | null>(null);
  const [createTeamInviteError, setCreateTeamInviteError] = useState<string | null>(null);
  const [teamInviteResolveError, setTeamInviteResolveError] = useState<string | null>(null);
  const [acceptTeamInviteError, setAcceptTeamInviteError] = useState<string | null>(null);
  const [updateTeamError, setUpdateTeamError] = useState<string | null>(null);
  const [leaveTeamError, setLeaveTeamError] = useState<string | null>(null);
  const [teamMemberActionError, setTeamMemberActionError] = useState<string | null>(null);
  const [teamProjectActionError, setTeamProjectActionError] = useState<string | null>(null);
  const [updateTeamShareSettingsError, setUpdateTeamShareSettingsError] = useState<string | null>(
    null
  );
  const { createTeam: createTeamMutation, isCreatingTeam } = useCreateTeam();
  const { createTeamInvite: createTeamInviteMutation, isCreatingTeamInvite } =
    useCreateTeamInvite();
  const { resolveTeamInvite: resolveTeamInviteMutation, isResolvingTeamInvite } =
    useResolveTeamInvite();
  const { acceptTeamInvite: acceptTeamInviteMutation, isAcceptingTeamInvite } =
    useAcceptTeamInvite();
  const { updateTeam: updateTeamMutation, isUpdatingTeam } = useUpdateTeam();
  const { leaveTeam: leaveTeamMutation, isLeavingTeam } = useLeaveTeam();
  const { deleteTeam: deleteTeamMutation, isDeletingTeam } = useDeleteTeam();
  const { updateTeamMemberRole: updateTeamMemberRoleMutation, isUpdatingTeamMemberRole } =
    useUpdateTeamMemberRole();
  const { removeTeamMember: removeTeamMemberMutation, isRemovingTeamMember } =
    useRemoveTeamMember();
  const { transferTeamOwnership: transferTeamOwnershipMutation, isTransferringTeamOwnership } =
    useTransferTeamOwnership();
  const { updateTeamShareSettings: updateTeamShareSettingsMutation, isUpdatingTeamShareSettings } =
    useUpdateTeamShareSettings();
  const { createTeamBoard: createTeamBoardMutation, isCreatingTeamBoard } = useCreateTeamBoard();
  const { updateTeamBoard: updateTeamBoardMutation, isUpdatingTeamBoard } = useUpdateTeamBoard();
  const { deleteTeamBoard: deleteTeamBoardMutation, isDeletingTeamBoard } =
    useDeleteTeamBoard();

  const userId = user.userId || '';
  const teamsQuery = useTeams({ userId });
  const teamQuery = useTeam({ userId, teamId });

  const teamListError = useMemo(() => {
    if (!teamsQuery.error) {
      return null;
    }

    return parseTeamError(teamsQuery.error, "Couldn't load your teams — try again?");
  }, [teamsQuery.error]);

  const teamDetailError = useMemo(() => {
    if (!teamQuery.error) {
      return null;
    }

    return parseTeamError(teamQuery.error, "Couldn't load this team — try again?");
  }, [teamQuery.error]);

  const createTeam = useCallback(
    async (teamName: string) => {
      const normalizedTeamName = teamName.trim();

      setCreateTeamError(null);

      try {
        const data = await createTeamMutation({ name: normalizedTeamName });
        return data.teamId || null;
      } catch (error) {
        setCreateTeamError(parseTeamError(error, "Couldn't create your team — try again?"));
        return null;
      }
    },
    [createTeamMutation]
  );

  const clearCreateTeamError = useCallback(() => {
    setCreateTeamError(null);
  }, []);

  const createTeamInvite = useCallback(
    async (targetTeamId: string, inviteEmail: string, role: 'admin' | 'member' = 'member') => {
      setCreateTeamInviteError(null);

      try {
        const { inviteUrl } = await createTeamInviteMutation({
          teamId: targetTeamId,
          teamInviteCreateReq: {
            email: inviteEmail.trim().toLowerCase(),
            role,
          },
        });

        return inviteUrl || null;
      } catch (error) {
        setCreateTeamInviteError(
          parseTeamError(error, "Couldn't generate an invite link — try again?")
        );
        return null;
      }
    },
    [createTeamInviteMutation]
  );

  const clearCreateTeamInviteError = useCallback(() => {
    setCreateTeamInviteError(null);
  }, []);

  const resolveTeamInvite = useCallback(
    async (inviteCode: string) => {
      const normalizedInviteCode = inviteCode.trim();
      setTeamInviteResolveError(null);

      try {
        return await resolveTeamInviteMutation(normalizedInviteCode);
      } catch (error) {
        setTeamInviteResolveError(
          parseTeamError(error, "Couldn't load invite details — try again?")
        );
        return null;
      }
    },
    [resolveTeamInviteMutation]
  );

  const clearTeamInviteResolveError = useCallback(() => {
    setTeamInviteResolveError(null);
  }, []);

  const acceptTeamInvite = useCallback(
    async (teamInviteAcceptReq: TeamInviteAcceptReq) => {
      setAcceptTeamInviteError(null);

      try {
        const { teamId: acceptedTeamId } = await acceptTeamInviteMutation(teamInviteAcceptReq);
        return acceptedTeamId || null;
      } catch (error) {
        setAcceptTeamInviteError(
          parseTeamError(error, "Couldn't accept that invite — try again?")
        );
        return null;
      }
    },
    [acceptTeamInviteMutation]
  );

  const clearAcceptTeamInviteError = useCallback(() => {
    setAcceptTeamInviteError(null);
  }, []);

  const updateTeamName = useCallback(
    async (targetTeamId: string, teamName: string) => {
      const normalizedTeamName = teamName.trim();
      setUpdateTeamError(null);

      try {
        const updatedTeam = await updateTeamMutation({
          teamId: targetTeamId,
          teamUpdateReq: {
            name: normalizedTeamName,
          },
        });

        return updatedTeam;
      } catch (error) {
        setUpdateTeamError(parseTeamError(error, "Couldn't update your team name — try again?"));
        return null;
      }
    },
    [updateTeamMutation]
  );

  const clearUpdateTeamError = useCallback(() => {
    setUpdateTeamError(null);
  }, []);

  const updateTeamShareSettings = useCallback(
    async (targetTeamId: string, teamShareSettingsUpdateReq: TeamShareSettingsUpdateReq) => {
      setUpdateTeamShareSettingsError(null);

      try {
        const membership = await updateTeamShareSettingsMutation({
          teamId: targetTeamId,
          teamShareSettingsUpdateReq,
        });

        return membership;
      } catch (error) {
        setUpdateTeamShareSettingsError(
          parseTeamError(error, "Couldn't update your sharing settings — try again?")
        );
        return null;
      }
    },
    [updateTeamShareSettingsMutation]
  );

  const clearUpdateTeamShareSettingsError = useCallback(() => {
    setUpdateTeamShareSettingsError(null);
  }, []);

  const leaveTeam = useCallback(
    async (targetTeamId: string) => {
      setLeaveTeamError(null);

      try {
        await leaveTeamMutation(targetTeamId);
        return true;
      } catch (error) {
        setLeaveTeamError(parseTeamError(error, "Couldn't leave this team — try again?"));
        return false;
      }
    },
    [leaveTeamMutation]
  );

  const clearLeaveTeamError = useCallback(() => {
    setLeaveTeamError(null);
  }, []);

  const deleteTeam = useCallback(
    async (targetTeamId: string) => {
      setLeaveTeamError(null);

      try {
        await deleteTeamMutation(targetTeamId);
        return true;
      } catch (error) {
        setLeaveTeamError(parseTeamError(error, "Couldn't delete this team — try again?"));
        return false;
      }
    },
    [deleteTeamMutation]
  );

  const updateTeamMemberRole = useCallback(
    async (targetTeamId: string, memberUserId: string, role: Exclude<TeamRole, 'owner'>) => {
      setTeamMemberActionError(null);

      try {
        const membership = await updateTeamMemberRoleMutation({
          teamId: targetTeamId,
          memberUserId,
          teamMemberRoleUpdateReq: {
            role,
          },
        });

        return membership;
      } catch (error) {
        setTeamMemberActionError(
          parseTeamError(error, "Couldn't update that member's role — try again?")
        );
        return null;
      }
    },
    [updateTeamMemberRoleMutation]
  );

  const removeTeamMember = useCallback(
    async (targetTeamId: string, memberUserId: string) => {
      setTeamMemberActionError(null);

      try {
        const membership = await removeTeamMemberMutation({
          teamId: targetTeamId,
          memberUserId,
        });

        return membership;
      } catch (error) {
        setTeamMemberActionError(
          parseTeamError(error, "Couldn't remove that member — try again?")
        );
        return null;
      }
    },
    [removeTeamMemberMutation]
  );

  const transferTeamOwnership = useCallback(
    async (targetTeamId: string, newOwnerUserId: string) => {
      setTeamMemberActionError(null);

      try {
        await transferTeamOwnershipMutation({
          teamId: targetTeamId,
          teamOwnershipTransferReq: {
            newOwnerUserId,
          },
        });

        return true;
      } catch (error) {
        setTeamMemberActionError(
          parseTeamError(error, "Couldn't transfer ownership — try again?")
        );
        return false;
      }
    },
    [transferTeamOwnershipMutation]
  );

  const clearTeamMemberActionError = useCallback(() => {
    setTeamMemberActionError(null);
  }, []);

  const createTeamBoard = useCallback(
    async (targetTeamId: string, boardName: string) => {
      setTeamProjectActionError(null);

      try {
        await createTeamBoardMutation({
          teamId: targetTeamId,
          teamBoardCreateReq: { name: boardName.trim() },
        });
        return true;
      } catch (error) {
        setTeamProjectActionError(
          parseTeamError(error, "Couldn't create that project — try again?")
        );
        return false;
      }
    },
    [createTeamBoardMutation]
  );

  const updateTeamBoard = useCallback(
    async (targetTeamId: string, boardId: string, boardName: string) => {
      setTeamProjectActionError(null);

      try {
        await updateTeamBoardMutation({
          teamId: targetTeamId,
          boardId,
          teamBoardUpdateReq: { name: boardName.trim() },
        });
        return true;
      } catch (error) {
        setTeamProjectActionError(
          parseTeamError(error, "Couldn't update that project — try again?")
        );
        return false;
      }
    },
    [updateTeamBoardMutation]
  );

  const deleteTeamBoard = useCallback(
    async (targetTeamId: string, boardId: string) => {
      setTeamProjectActionError(null);

      try {
        await deleteTeamBoardMutation({ teamId: targetTeamId, boardId });
        return true;
      } catch (error) {
        setTeamProjectActionError(
          parseTeamError(error, "Couldn't delete that project — try again?")
        );
        return false;
      }
    },
    [deleteTeamBoardMutation]
  );

  const clearTeamProjectActionError = useCallback(() => {
    setTeamProjectActionError(null);
  }, []);

  const selectedTeam = teamQuery.team;
  const selectedMembership = teamQuery.membership;
  const selectedMembers = teamQuery.members;
  const selectedBoards = teamQuery.boards;
  const isTeamsLoading = isUserFetching || !userId || teamsQuery.isLoading;
  const isTeamLoading = Boolean(teamId) && (isUserFetching || !userId || teamQuery.isLoading);

  return (
    <TeamContext.Provider
      value={{
        teams: teamsQuery.teams,
        selectedTeam,
        selectedMembership,
        selectedMembers,
        selectedBoards,
        activeTeamName: selectedTeam?.name ?? null,
        hasTeams: teamsQuery.teams.length > 0,
        createTeamError,
        createTeamInviteError,
        teamInviteResolveError,
        acceptTeamInviteError,
        updateTeamError,
        leaveTeamError,
        teamMemberActionError,
        teamProjectActionError,
        isCreatingTeam,
        isCreatingTeamInvite,
        isResolvingTeamInvite,
        isAcceptingTeamInvite,
        isTeamsLoading,
        isTeamLoading,
        teamListError,
        teamDetailError,
        updateTeamShareSettingsError,
        createTeam,
        createTeamInvite,
        resolveTeamInvite,
        acceptTeamInvite,
        updateTeamName,
        leaveTeam,
        deleteTeam,
        updateTeamMemberRole,
        removeTeamMember,
        transferTeamOwnership,
        createTeamBoard,
        updateTeamBoard,
        deleteTeamBoard,
        updateTeamShareSettings,
        clearCreateTeamError,
        clearCreateTeamInviteError,
        clearTeamInviteResolveError,
        clearAcceptTeamInviteError,
        clearUpdateTeamError,
        clearLeaveTeamError,
        clearTeamMemberActionError,
        clearTeamProjectActionError,
        clearUpdateTeamShareSettingsError,
        isUpdatingTeamShareSettings,
        isUpdatingTeam,
        isLeavingTeam,
        isDeletingTeam,
        isUpdatingTeamMemberRole,
        isRemovingTeamMember,
        isTransferringTeamOwnership,
        isCreatingTeamBoard,
        isUpdatingTeamBoard,
        isDeletingTeamBoard,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};
